import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import type { AxiosError, AxiosResponse } from 'axios';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';

// components
import ReactCodeInput from 'react-code-input';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox as CheckboxBase,
} from '@mui/material';

import { InputLabelBase } from '../../../inputs/InputLabelBase';
import { PasswordField as PasswordFieldBase } from '../../../inputs/text-fields/passwords/PasswordField';

import { withFormController } from '../../../hocs/forms';

// other
import { login } from '../../../../api/auth/auth.api';
import {
  cacheStaffLoginTokens,
  setIsDeviceRemembered,
} from '../../../../utils/next/auth.utils';
import { Show } from '../../../Show';
import { parseLoginError } from './util';
import { LoginFormType, LoginFormValues } from '../types';
import { StyledLoginButton } from './helpers';
import { consumeOAuthRedirect } from '../oauthRedirect.utils';
import {
  getPostLoginDestination,
  type LoginLocationState,
} from '../postLoginDestination';
import { useMagicLink } from '../hooks/useMagicLink';
import { useRecaptcha } from '../../../hooks/useRecaptcha';

const PasswordField = withFormController(PasswordFieldBase);
const Checkbox = withFormController(CheckboxBase);

const EmailForm = () => {
  const methods = useFormContext<LoginFormValues>();
  const { handleSubmit, watch, getValues, setValue, formState } = methods;
  const history = useHistory();
  const location = useLocation<LoginLocationState>();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [resendFlag, setResendFlag] = React.useState(false);
  const [oauthComplete, setOauthComplete] = React.useState(false);

  const [mfaEnabled, mfaCode, sso] = watch(['mfaEnabled', 'mfaCode', 'sso']);

  const {
    state: magicLinkState,
    send: sendMagicLink,
    cooldownSeconds,
  } = useMagicLink();
  const {
    token: captchaToken,
    executeChallenge,
    resetToken: resetCaptchaToken,
    getElementProps,
  } = useRecaptcha();
  const magicLinkEnabled = useFeatureIsOn(FEATURE_FLAG_KEYS.MAGIC_LINK_LOGIN);
  const showMagicLink =
    magicLinkEnabled && !sessionStorage.getItem('mcp_oauth_redirect');

  const handleMagicLinkClick = async () => {
    const challengeResult = await Promise.resolve(executeChallenge());
    const captcha = (challengeResult as string | void) ?? captchaToken;
    const email = getValues('username');
    if (!email || !captcha) return;
    try {
      await sendMagicLink(email, captcha as string);
    } finally {
      // reCAPTCHA v3/invisible tokens are single-use. Reset so the next hover's
      // executeChallenge produces a fresh token, otherwise "Send another link"
      // or "Try again" reuses the stale (server-consumed) token and the second
      // request fails. Mirrors ForgotPassword.tsx's post-submit pattern.
      resetCaptchaToken();
    }
  };

  const onSubmit = async (formValues: LoginFormValues) => {
    const { rememberDevice, sso, mfaEnabled, ...loginPayload } = formValues;

    try {
      const resp: AxiosResponse<{ accessToken: string; refreshToken: string }> =
        await login({
          data: loginPayload,
        });

      const oauthRedirect = consumeOAuthRedirect();
      if (oauthRedirect) {
        setOauthComplete(true);
        window.location.replace(oauthRedirect);
        return;
      }

      setIsDeviceRemembered(rememberDevice);
      cacheStaffLoginTokens(resp.data.accessToken, resp.data.refreshToken);

      history.replace(getPostLoginDestination(location.state));
    } catch (err) {
      const parsed = parseLoginError(err as AxiosError);

      switch (parsed.type) {
        case 'passwordExpired':
          history.push('/password-expired', { email: loginPayload.username });
          break;
        case 'mfaRequired':
          setValue('mfaEnabled', true);
          setValue('mfaType', parsed.mfaType);
          setErrorMessage('');
          break;
        case 'validationRequired':
          setErrorMessage(parsed.message);
          setResendFlag(true);
          break;
        default:
          setErrorMessage(parsed.message);
      }
    }
  };

  const handleMfaCodeChange = (code: string) => {
    methods.setValue('mfaCode', code);
  };

  if (oauthComplete) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={6}
        gap={2}
      >
        <Typography variant="h6" style={{ color: '#fff' }}>
          Authentication complete
        </Typography>
        <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.7)' }}>
          You can close this browser tab and return to Cursor.
        </Typography>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <InputLabelBase style={{ paddingTop: 0, color: '#fff' }}>
        Password
      </InputLabelBase>
      <PasswordField
        name="password"
        // @ts-expect-error TS(2339): Property 'placeholder' does not exist on type '{}'.
        placeholder="Please enter password"
        required
      />

      <Show when={mfaEnabled}>
        <InputLabelBase style={{ color: '#fff' }}>
          Multi-Factor Code
        </InputLabelBase>
        {/* @ts-expect-error TS(2339): Property 'type' does not exist on type '{}'. */}
        <ReactCodeInput
          type="text"
          name="mfa"
          fields={6}
          onChange={handleMfaCodeChange}
        />
      </Show>

      <Box mt={1}>
        <FormControlLabel
          control={
            <Checkbox
              name="rememberDevice"
              defaultChecked={methods.getValues('rememberDevice')}
              color="secondary"
            />
          }
          label={
            <Typography variant="caption" style={{ color: '#fff' }}>
              Remember this device
            </Typography>
          }
        />
      </Box>

      <StyledLoginButton
        disabled={
          (mfaEnabled && mfaCode?.length !== 6) || formState.isSubmitting
        }
        size="large"
        fullWidth
        type="submit"
      >
        {formState.isSubmitting ? 'Loading...' : 'Login'}
      </StyledLoginButton>

      {/*
        reCAPTCHA container. Must be rendered unconditionally — useRecaptcha's
        mount effect calls grecaptcha.render() with deps=[widgetId] and runs
        exactly once. If the div is gated on `showMagicLink` and GrowthBook
        hasn't resolved the flag yet at mount, the effect fires against an
        absent DOM element, fails silently, and never retries. By the time
        the flag flips true and the div appears, the widget is permanently
        un-initialized and captchaToken stays null forever. Rendering the
        container always is cheap (reCAPTCHA v3 is invisible) and matches
        ForgotPassword.tsx's pattern.
      */}
      <div {...getElementProps()} />

      {showMagicLink &&
        (magicLinkState.status === 'idle' ||
          magicLinkState.status === 'error' ||
          magicLinkState.status === 'rate_limited') && (
          <Box mt={2}>
            {magicLinkState.status === 'rate_limited' && (
              <Typography mb={1} style={{ color: '#fa8072' }}>
                Too many requests. Please try again later.
              </Typography>
            )}
            {magicLinkState.status === 'error' && (
              <Typography mb={1} style={{ color: '#fa8072' }}>
                Something went wrong. Please try again.
              </Typography>
            )}
            <Button
              variant="outlined"
              fullWidth
              onClick={handleMagicLinkClick}
              onMouseEnter={() => executeChallenge()}
              sx={{ color: '#fff', borderColor: '#fff' }}
            >
              {magicLinkState.status === 'idle'
                ? 'Email me a magic link'
                : 'Try again'}
            </Button>
            {magicLinkState.status === 'idle' && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: 'block', color: 'rgba(255,255,255,0.7)' }}
              >
                We&apos;ll send a one-time sign-in link to your inbox.
                Requesting a new link invalidates any previous links.
              </Typography>
            )}
          </Box>
        )}

      {showMagicLink && magicLinkState.status === 'sending' && (
        <Typography mt={2} style={{ color: '#fff' }}>
          Sending…
        </Typography>
      )}

      {showMagicLink && magicLinkState.status === 'sso_required' && (
        <Box mt={3}>
          <Typography style={{ color: '#fff' }}>
            This account uses Single Sign-On. Please sign in with SSO instead.
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            href="/login"
            sx={{ mt: 2, color: '#fff', borderColor: '#fff' }}
          >
            Sign in with SSO
          </Button>
        </Box>
      )}

      {showMagicLink && magicLinkState.status === 'sent' && (
        <Box mt={3}>
          <Typography style={{ color: '#fff' }}>
            Check your inbox. If an account exists for{' '}
            <b>{magicLinkState.email}</b>, we&apos;ve sent a magic link (also
            check your spam folder).
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            disabled={cooldownSeconds > 0}
            onClick={handleMagicLinkClick}
            onMouseEnter={() => executeChallenge()}
            sx={{ mt: 2, color: '#fff', borderColor: '#fff' }}
          >
            {cooldownSeconds > 0
              ? `Send another link (${cooldownSeconds}s)`
              : 'Send another link'}
          </Button>
        </Box>
      )}

      <Show when={!!errorMessage}>
        <p style={{ color: '#fa8072' }}>{errorMessage}</p>
        <Show when={resendFlag}>
          <Link to="/resend-validation" className="link-underlined">
            Resend Validation Email
          </Link>
        </Show>
      </Show>

      <Box mt={6} display="flex" alignItems="center" gap={2}>
        <Link
          to="/forgot-password"
          className="link-underlined"
          style={{ color: '#fff' }}
        >
          Forgot Password
        </Link>

        <Show when={mfaEnabled}>
          <Link
            to={{
              pathname: '/forgot-password',
              state: {
                username: getValues('username'),
                password: getValues('password'),
              },
            }}
            className="link-underlined"
            style={{ color: '#fff' }}
          >
            Reset MFA
          </Link>
        </Show>

        <Show when={sso.enabled}>
          <Link
            to="#"
            className="link-underlined"
            style={{ color: '#fff' }}
            onClick={() => {
              methods.setValue('sso.loginFormType', LoginFormType.SSO);
            }}
          >
            Login with SSO
          </Link>
        </Show>
      </Box>
    </form>
  );
};

export default EmailForm;
